import { useContext, useMemo } from 'react';
import { AuthContext } from '../auth/Auth';
import { useLoginState } from '../studioxr/auth/LoginState';
import { firebaseHelper, usePermissions, useDesignFirm } from 'ui';
import { UserProfileRole } from 'ts-interfaces';

/**
 * Hook to check if user has Teams PRO access
 * Teams PRO = PRO user + part of a design firm
 * Regular PRO = PRO user + NOT part of a design firm
 */
export const useTeamsProAccess = () => {
  const { currentUser } = useContext(AuthContext);
  const profile = useLoginState((state) => state.profile);
  const permissions = usePermissions(currentUser?.uid, firebaseHelper.db);
  const designFirm = useDesignFirm(firebaseHelper.db, permissions?.permissions?.teamsDesigner?.[0], null);

  // Memoize the expensive calculations to prevent unnecessary re-calculations
  const accessStatus = useMemo(() => {
    // Check if user is PRO (using type assertion to match Bar.jsx pattern)
    const isProUser = profile && (profile as any)?.userRole === UserProfileRole.PRO;
    
    // Check if user is part of a design firm (Teams PRO)
    const isTeamsProUser = isProUser && Boolean(designFirm?.firm?.name);
    
    // Regular PRO user (PRO but not in a team)
    const isRegularProUser = isProUser && !designFirm?.firm?.name;

    return {
      isProUser,
      isTeamsProUser,
      isRegularProUser,
      designFirm
    };
  }, [
    (profile as any)?.userRole, // Use type assertion for the dependency as well
    designFirm?.firm?.name, // Only watch the firm name, not the entire designFirm object
    designFirm // Include the full designFirm in dependencies but it should be more stable
  ]);

  return accessStatus;
}; 